import { html, nothing, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connectedInputs } from '../../../core/domain/model';
import { ModelSectionBase } from './model-section-base';
import { t } from '@vitesse/base-ui/i18n';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { isChartableValue, getChartTheme } from '../../shell/symbolGrid/chartUtils';
import { iconButton } from '@vitesse/base-ui/components/base-buttons';
import type { StreamFrame } from '../../../core/bridge/types';
import type { TypedValue } from '../../../core/bridge/connect-client';
import { RingBuffer } from '../chart/ring-buffer';
import {
  DEFAULT_CHART_CONFIG,
  formatValue,
  type ChartConfig,
  type ChartGhost,
} from '../chart/chart-types';
import '../chart/vitesse-sparkline';
import '../chart/vitesse-chart';
import '../chart/vitesse-chart-config-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-model-variables> — the model's INPUTS (bound arguments) + OUTPUTS
 * (published variables), each a symbol-tree-style row: name · live value · inline
 * sparkline; click to expand into the full <vitesse-chart> (axes, smooth scroll,
 * current-value, crosshair) with pause · ghost-capture · settings. Any number of
 * rows expand at once. The agent's model-variables UX, on the new substrate.
 *
 * Data path (all protobuf, viewport-scoped, released on leave): a batched
 * historian queryRange SEED per scope paints the window immediately, then LIVE
 * streams keep it current — outputs via this model's Values stream,
 * controller-bound inputs via the bound controller's windowed grid stream,
 * model-bound inputs (composition) via the SOURCE model's Values stream.
 *
 * The fix that makes it smooth: each row owns ONE RingBuffer, shared BY REFERENCE
 * with whichever chart element is mounted. Live ticks push into the ring; the
 * chart/sparkline read it on the shared animation clock. Nothing re-seeds a chart
 * per value and no ref churns — collapse↔expand never loses history. Per-row
 * chart config (theme/window/sample-rate/name/y-axis/ghosts) persists to
 * localStorage; the y-axis is anchored by a running min/max so it never bounces.
 */
interface Row {
  kind: 'input' | 'output';
  id: string; // variable id (outputs) / input name (inputs)
  /** Bound entity path (inputs only): controllers/<cid>/symbols/<sid> or models/<mid>/variables/<vid>. */
  boundPath?: string;
  /** Live-routing key, unique per data source (see buildRows). */
  liveKey: string;
}

@customElement('vitesse-model-variables')
export class ModelVariablesSection extends ModelSectionBase {
  @state() private rows: Row[] = [];
  @state() private expandedRows = new Set<string>();
  /** rowId → pausedTimestamp (present = paused/frozen). */
  @state() private pausedRows = new Map<string, number>();
  /** rowId → config dialog open. */
  @state() private configOpen = new Set<string>();

  private valueText = new Map<string, string>(); // row id → display text
  private bufs = new Map<string, RingBuffer>(); // row id → shared ring
  private configs = new Map<string, ChartConfig>(); // row id → persisted config
  private preview = new Map<string, ChartConfig>(); // row id → live preview (dialog open)
  private ports: MessagePort[] = [];
  private wiredFor = '';
  private textDirty = false;

  protected sectionHeading(): string {
    return t('model.variables');
  }
  protected sectionClass(): string {
    return 'model-variables-section';
  }

  protected willUpdate(changed: PropertyValues): void {
    super.willUpdate?.(changed);
    const m = this.model;
    // status is part of the key: start/stop must re-wire, because a stopped
    // model's panel holds NO live streams at all (see wire()).
    const key = m ? `${this.modelId}:${m.status}:${m.outputs.join(',')}:${JSON.stringify(connectedInputs(m))}` : '';
    if (m && key !== this.wiredFor) {
      this.wiredFor = key;
      this.teardown();
      this.buildRows();
      void this.wire();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardown();
    this.wiredFor = '';
  }

  private teardown(): void {
    // Close BOTH ends: post {kind:'close'} so the worker drops the upstream stream,
    // AND close the main-thread port handle (else dead handles + their onmessage
    // closures accumulate across model re-wires / page revisits).
    for (const p of this.ports) {
      p.postMessage({ kind: 'close' });
      p.close();
    }
    this.ports = [];
    // Clear EVERY row-keyed map — a re-wire (model edit) or revisit rebuilds rows
    // under new ids; without this the old ids' configs/preview/paused/open state
    // lingered.
    this.valueText.clear();
    this.bufs.clear();
    this.configs.clear();
    this.preview.clear();
    if (this.configOpen.size) this.configOpen = new Set();
    if (this.pausedRows.size) this.pausedRows = new Map();
  }

  // ── rows ────────────────────────────────────────────────────────────────────
  private buildRows(): void {
    const m = this.model;
    if (!m) {
      this.rows = [];
      return;
    }
    // The root's connected inputs: a group's children read their own, which are not the model's inputs.
    const inputs: Row[] = Object.entries(connectedInputs(m)).map(([name, path]) => {
      const segs = path.split('/');
      let liveKey = path;
      // A wired path names its symbol by the display path (ModelDescriptor.symbol_paths),
      // the form the whole value plane speaks: subscribeTypedValues/queryRange encode
      // on send and decode on receive.
      if (path.startsWith('controllers/')) liveKey = segs.slice(3).join('/');
      else if (path.startsWith('models/')) liveKey = `${segs[1]}/${segs[3]}`; // <srcMid>/<vid>
      return { kind: 'input' as const, id: name, boundPath: path, liveKey };
    });
    const outputs: Row[] = m.outputs.map((vid) => ({ kind: 'output' as const, id: vid, liveKey: vid }));
    this.rows = [...inputs, ...outputs];
    for (const r of this.rows) if (!this.bufs.has(r.id)) this.bufs.set(r.id, new RingBuffer());
  }

  // ── per-row ring / config helpers ───────────────────────────────────────────
  private ringFor(id: string): RingBuffer {
    let r = this.bufs.get(id);
    if (!r) this.bufs.set(id, (r = new RingBuffer()));
    return r;
  }
  private configFor(id: string): ChartConfig {
    let c = this.configs.get(id);
    if (!c) {
      c = this.loadConfig(id);
      this.configs.set(id, c);
    }
    return c;
  }
  private effectiveConfig(id: string): ChartConfig {
    return this.preview.get(id) ?? this.configFor(id);
  }
  private configKey(id: string): string {
    return `vc-cfg:${this.modelId}:${id}`;
  }
  private loadConfig(id: string): ChartConfig {
    try {
      const raw = localStorage.getItem(this.configKey(id));
      if (raw) return { ...DEFAULT_CHART_CONFIG, theme: getChartTheme(), ...(JSON.parse(raw) as ChartConfig) };
    } catch {
      /* fall through to default */
    }
    return { ...DEFAULT_CHART_CONFIG, theme: getChartTheme() };
  }
  private saveConfig(id: string, c: ChartConfig): void {
    this.configs.set(id, c);
    try {
      localStorage.setItem(this.configKey(id), JSON.stringify(c));
    } catch {
      /* storage full / disabled — config stays in-memory for the session */
    }
    this.requestUpdate();
  }

  // ── wiring: seed buffers + open live streams (unchanged contract) ────────────
  private async wire(): Promise<void> {
    const m = this.model;
    const mgr = this.models;
    if (!m || !mgr) return;
    // A DEAD model's panel is silent — no streams, no seeds, dashes. The input
    // rows subscribe to the bound SOURCES (a controller's live feed), which keep
    // publishing whether or not this model runs; unstated, that painted a rising
    // part-counter under a model whose status said stopped — the source's truth
    // presented as the model's. A stopped model reads nothing, so its panel
    // shows nothing. Start → status flips → willUpdate re-wires → the snapshot
    // phase paints every row at once. The gate is on the DEAD set, not
    // status==='running': waiting/paused/starting are alive runtimes that hold
    // and consume values.
    if (m.status === 'stopped' || m.status === 'error' || m.status === 'unknown') return;
    const mid = this.modelId;

    const seed = (
      promise: Promise<{ symbol: string; tsMs: number; num: number | null }[]>,
      keyOf: (symbol: string) => string,
    ): void => {
      void promise
        .then((samples) => {
          if (this.modelId !== mid) return;
          this.applySeed(samples.map((s) => ({ key: keyOf(s.symbol), tsMs: s.tsMs, num: s.num })));
        })
        .catch(() => void 0); // historian briefly unavailable → live-only charts
    };
    const live = (promise: Promise<MessagePort>, kind: Row['kind'], keyOf: (symbolId: string) => string): void => {
      void promise.then((port) => {
        if (this.modelId !== mid) {
          port.postMessage({ kind: 'close' });
          return;
        }
        this.ports.push(port);
        port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => this.onLive(e.data, kind, keyOf);
        port.start();
      });
    };

    if (m.outputs.length > 0) {
      seed(mgr.queryHistory(mid, m.outputs), (s) => s);
      live(mgr.subscribeValues(mid), 'output', (s) => s);
    }

    const byController = new Map<string, string[]>();
    const bySourceModel = new Map<string, string[]>();
    for (const r of this.rows) {
      if (r.kind !== 'input' || !r.boundPath) continue;
      const segs = r.boundPath.split('/');
      if (r.boundPath.startsWith('controllers/')) {
        const cid = segs[1];
        (byController.get(cid) ?? byController.set(cid, []).get(cid)!).push(r.liveKey);
      } else if (r.boundPath.startsWith('models/')) {
        const srcMid = segs[1];
        (bySourceModel.get(srcMid) ?? bySourceModel.set(srcMid, []).get(srcMid)!).push(segs[3]);
      }
    }
    for (const [cid, paths] of byController) {
      seed(mgr.queryBoundHistory(cid, paths), (s) => s);
      live(mgr.subscribeBoundValues(cid, paths), 'input', (s) => s);
    }
    for (const [srcMid, vids] of bySourceModel) {
      seed(mgr.queryHistory(srcMid, vids), (s) => `${srcMid}/${s}`);
      live(mgr.subscribeValues(srcMid), 'input', (s) => `${srcMid}/${s}`);
    }
  }

  private applySeed(samples: { key: string; tsMs: number; num: number | null }[]): void {
    const byKey = new Map<string, { tsMs: number; num: number }[]>();
    for (const s of samples) {
      if (s.num == null) continue;
      (byKey.get(s.key) ?? byKey.set(s.key, []).get(s.key)!).push({ tsMs: s.tsMs, num: s.num });
    }
    for (const [key, arr] of byKey) {
      arr.sort((a, b) => a.tsMs - b.tsMs);
      for (const row of this.rows) {
        if (row.liveKey !== key) continue;
        // LOCF-densify the sparse publish-on-change seed to the live density, so the
        // bulk-seeded left region scrolls off smoothly instead of in big chunks.
        this.ringFor(row.id).seedDense(arr, 50, Date.now());
        if (!this.valueText.has(row.id) && arr.length > 0) this.valueText.set(row.id, formatValue(arr[arr.length - 1].num));
      }
    }
    this.requestUpdate();
  }

  private onLive(frame: StreamFrame<TypedValue>, kind: Row['kind'], keyOf: (symbolId: string) => string): void {
    if (frame.kind === 'snapshot') {
      for (const item of frame.items) this.applyValue(item, kind, keyOf);
      this.scheduleText();
      return;
    }
    if (frame.kind !== 'delta') return;
    this.applyValue(frame.item, kind, keyOf);
    this.scheduleText();
  }

  private applyValue(v: TypedValue, kind: Row['kind'], keyOf: (symbolId: string) => string): void {
    const key = keyOf(v.symbolId);
    for (const row of this.rows) {
      if (row.kind !== kind || row.liveKey !== key) continue;
      this.valueText.set(row.id, v.value == null ? '—' : typeof v.value === 'number' ? formatValue(v.value) : String(v.value));
      if (v.num != null && isChartableValue(v.num) && !this.pausedRows.has(row.id)) {
        this.ringFor(row.id).push(v.tsMs || Date.now(), v.num);
      }
    }
  }

  /** Coalesce value-text re-renders to one per frame (charts redraw themselves). */
  private scheduleText(): void {
    if (this.textDirty) return;
    this.textDirty = true;
    requestAnimationFrame(() => {
      this.textDirty = false;
      this.requestUpdate();
    });
  }

  // ── interactions ─────────────────────────────────────────────────────────────
  private toggleExpand(id: string): void {
    const next = new Set(this.expandedRows);
    if (!next.delete(id)) next.add(id);
    this.expandedRows = next;
  }
  private togglePause(id: string): void {
    const next = new Map(this.pausedRows);
    if (next.has(id)) next.delete(id);
    else next.set(id, Date.now());
    this.pausedRows = next;
  }
  private openConfig(id: string): void {
    this.configOpen = new Set(this.configOpen).add(id);
  }
  private closeConfig(id: string): void {
    const next = new Set(this.configOpen);
    next.delete(id);
    this.configOpen = next;
    this.preview.delete(id); // drop any live preview
    this.requestUpdate();
  }
  private onPreview(id: string, c: ChartConfig): void {
    this.preview.set(id, c);
    this.requestUpdate();
  }
  private onSave(id: string, c: ChartConfig): void {
    this.preview.delete(id);
    this.saveConfig(id, c);
    this.closeConfig(id);
  }
  /** Capture the current frozen window as a ghost overlay (paused-only). */
  private captureGhost(id: string): void {
    const paused = this.pausedRows.get(id);
    const ring = this.bufs.get(id);
    if (paused == null || !ring || ring.count === 0) return;
    const cfg = this.configFor(id);
    const start = paused - cfg.timeWindowMs;
    const data: { relTime: number; value: number }[] = [];
    for (let i = 0; i < ring.count; i++) {
      const idx = (ring.head + i) % ring.capacity;
      const ts = ring.timestamps[idx];
      if (ts < start) continue;
      data.push({ relTime: ts - paused, value: ring.values[idx] });
    }
    if (data.length === 0) return;
    const ghost: ChartGhost = {
      id: `g${Date.now()}`,
      data,
      capturedAt: paused,
      timeWindowMs: cfg.timeWindowMs,
      sampleRateHz: cfg.sampleRateHz,
    };
    this.saveConfig(id, { ...cfg, ghosts: [...(cfg.ghosts ?? []), ghost] });
  }

  // ── render ────────────────────────────────────────────────────────────────────
  protected renderContent(): unknown {
    const m = this.model;
    if (!m) return nothing;
    if (this.rows.length === 0) return html`<div class="model-empty">${t('model.noOutputs')}</div>`;

    return html`<div class="variables-container">
      ${this.rows.map((r) => this.renderRow(r))}
    </div>`;
  }

  private renderRow(r: Row) {
    const ring = this.ringFor(r.id);
    const hasChart = ring.count > 0;
    const expanded = this.expandedRows.has(r.id);
    const cfg = this.effectiveConfig(r.id);
    const paused = this.pausedRows.get(r.id) ?? null;

    return html`<div class="variable-entry ${expanded ? 'expanded' : 'collapsed'} ${hasChart ? 'has-chart' : 'no-chart'}">
      <div class="variable-row ${hasChart ? 'clickable' : ''}" @click=${hasChart ? () => this.toggleExpand(r.id) : undefined}>
        <span class="variable-expand-indicator">
          ${hasChart
            ? html`<span class="expand-icon ${expanded ? 'expanded' : 'collapsed'}">
                <vitesse-icon name="chevron-right" size="14"></vitesse-icon>
              </span>`
            : nothing}
        </span>
        <!-- Name, badge and value are the row's OWN items, siblings of the sparkline. Inside a
             shrinkable wrapper they spilled out of it when the tile was narrow, and the sparkline,
             laid out after the wrapper's edge, drew over them. As siblings, the row gives the space
             back in order: the value ellipsizes, then the sparkline narrows. -->
        <span class="variable-name">${r.id}</span>
        <!-- Which WAY the value flows: green feeds in, blue is published out.
             Directly AFTER the name, before the value — the names column gets a
             shared min-width, so the badges form their own aligned column and
             nothing about them moves on expand/collapse (fixed-width neighbours
             on both sides). -->
        <span class="variable-kind" title=${r.kind === 'input' ? (r.boundPath ?? '') : ''}>
          ${r.kind === 'input' ? statusBadge('green', t('model.inputTag')) : statusBadge('blue', t('model.outputTag'))}
        </span>
        <span class="variable-value">${this.valueText.get(r.id) ?? '—'}</span>
        ${hasChart && !expanded
          ? html`<div class="variable-chart-inline">
              <vitesse-sparkline
                .ring=${ring}
                .timeWindowMs=${cfg.timeWindowMs}
                .sampleRateHz=${cfg.sampleRateHz}
                .theme=${cfg.theme ?? 'frost'}
              ></vitesse-sparkline>
            </div>`
          : nothing}
      </div>

      <div class="variable-chart-expanded-wrapper" style="grid-template-rows:${expanded ? '1fr' : '0fr'};opacity:${expanded ? 1 : 0}">
        <div class="variable-chart-expanded-overflow">
          ${expanded ? this.renderExpanded(r, ring, cfg, paused) : nothing}
        </div>
      </div>
    </div>`;
  }

  private renderExpanded(r: Row, ring: RingBuffer, cfg: ChartConfig, paused: number | null) {
    const id = r.id;
    return html`
      <div class="expanded-chart-controls">
        ${paused != null
          ? iconButton({ icon: 'ghost', title: t('chart.ghost'), onClick: () => this.captureGhost(id) })
          : nothing}
        ${iconButton({
          icon: paused != null ? 'play' : 'pause',
          title: paused != null ? t('chart.resume') : t('chart.pause'),
          active: paused != null,
          onClick: () => this.togglePause(id),
        })}
        ${iconButton({ icon: 'settings', title: t('chart.settings'), onClick: () => this.openConfig(id) })}
      </div>
      <div class="expanded-chart-container">
        <vitesse-chart
          .ring=${ring}
          .config=${cfg}
          .paused=${paused}
          .ghosts=${cfg.ghosts ?? []}
        ></vitesse-chart>
      </div>
      <vitesse-chart-config-dialog
        .open=${this.configOpen.has(id)}
        .config=${this.configFor(id)}
        .label=${id}
        @config-preview=${(e: CustomEvent<ChartConfig>) => this.onPreview(id, e.detail)}
        @config-save=${(e: CustomEvent<ChartConfig>) => this.onSave(id, e.detail)}
        @dialog-close=${() => this.closeConfig(id)}
      ></vitesse-chart-config-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-variables': ModelVariablesSection;
  }
}
